import { memo, useCallback } from 'react';
import styled, { css } from 'styled-components';

import type { CommonCardProps } from '@wecasa/common/src/types/components';
import colors from '@wecasa/common/src/utils/colors';

import { useOnClickThrottled } from '@wecasa/react-hooks/src/use-on-click-throttled';

const disabledStyle = css`
  opacity: 0.6;
`;

const pressableStyle = css`
  cursor: pointer;
  transition: background-color 0.2s ease;
`;

const hoverStyle = css`
  /*
   * https://stackoverflow.com/questions/23885255/how-to-remove-ignore-hover-css-style-on-touch-devices
   * The goal here is to remove the hover effect on mobile because it causes some weird behaviors on
   * chrome
   */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${colors.neutral20};
    }
  }
`;

const StyledCard = styled.div<{
  $color?: string;
  $borderRadius?: number;
  $isPressable: boolean;
  $isDisabled: boolean;
  $selected: boolean;
}>`
  display: flex;
  box-sizing: border-box;
  width: auto;
  outline: none;
  ${({ $color, $borderRadius, $isPressable, $isDisabled, $selected }) => css`
    flex-direction: column;
    background-color: ${$color || colors.neutral10};
    border-radius: ${$borderRadius || 12}px;
    ${$isPressable && pressableStyle}
    ${$isPressable && !$selected && hoverStyle}
    ${$isDisabled && disabledStyle}
  `};
`;

export type CardProps = CommonCardProps & {
  ariaDisabled?: boolean;
  ariaSelected?: boolean;
  label?: string;
  className?: string;
  role?: string;
  borderRadius?: number;
  testId?: string;
};

const Card = ({
  ariaDisabled,
  ariaSelected,
  label,
  onPress,
  selected = false,
  isDisabled = false,
  className,
  role = 'button',
  borderRadius,
  color,
  children,
  testId,
}: CardProps) => {
  const isPressable = !!onPress && !isDisabled;
  const tabIndexToSet = isPressable ? 0 : undefined;

  const onClickThrottled = useOnClickThrottled(isPressable ? onPress : undefined);
  const onKeyUp = useCallback(
    // @ts-ignore TODO: set typing
    (e) => {
      // Space key
      if (e.keyCode === 32) {
        onClickThrottled();
      }
    },
    [onClickThrottled],
  );

  const roleToSet = onPress ? role : undefined;
  const ariaChecked = (role === 'checkbox' || role === 'radio') && onPress ? selected : undefined;

  return (
    <StyledCard
      $isDisabled={isDisabled}
      $isPressable={isPressable}
      $selected={selected}
      $color={color}
      $borderRadius={borderRadius}
      aria-label={label}
      aria-checked={ariaChecked}
      /*
       * testing the presence of aria-disabled using `toBeDisabled` from @testing-library/react does
       * not work, it is only available for form controls https://github.com/testing-library/jest-dom#tobedisabled
       */
      aria-disabled={isDisabled || ariaDisabled}
      aria-selected={ariaSelected}
      onClick={onClickThrottled}
      role={roleToSet}
      className={className}
      tabIndex={tabIndexToSet}
      onKeyUp={onKeyUp}
      data-testid={testId}
    >
      {children}
    </StyledCard>
  );
};

export default memo(Card);
